import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, throwError } from "rxjs";
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { MasterHelpModel } from '../models/MasterHelp.model';
import { InfraService } from './infra.service';

@Injectable()
export class MasterHelpService {
  private configCoreUrl: string;
  private helpByIdRequests = new Map<number, Observable<MasterHelpModel>>();
  private helpByIdValues = new Map<number, MasterHelpModel>();
  constructor(private infraService: InfraService, private http: HttpClient) {
    this.infraService.infraDetails$.subscribe(data => {
      this.configCoreUrl = data.WebEndPoint2 + 'help';
      // Help rows are served per region, so anything cached against the previous
      // endpoint is no longer valid.
      this.helpByIdRequests.clear();
      this.helpByIdValues.clear();
    });

    if (!this.infraService.isInfraDetailsAvailable && localStorage.getItem('selectedInfra') != undefined) {
      this.infraService.isInfraDetailsAvailable = true;
      this.infraService.setInfraDetails(localStorage.getItem('selectedInfra'));
    }
  }

  public getMasterHelp(StepId: number): Observable<MasterHelpModel> {
    return this.http.get<MasterHelpModel>(`${this.configCoreUrl}/getHelpDescriptions/${StepId}`);
  }

  public getMasterHelpById(MasterHelpId: number): Observable<MasterHelpModel> {
    let request = this.helpByIdRequests.get(MasterHelpId);
    if (!request) {
      request = this.http.get<MasterHelpModel>(`${this.configCoreUrl}/getHelpDescriptionsById/${MasterHelpId}`)
        .pipe(
          tap(data => this.helpByIdValues.set(MasterHelpId, data)),
          catchError(error => {
            // A cached failure would never recover, so let the next caller retry.
            this.helpByIdRequests.delete(MasterHelpId);
            return throwError(() => error);
          }),
          shareReplay({ bufferSize: 1, refCount: false })
        );
      this.helpByIdRequests.set(MasterHelpId, request);
    }
    // Callers keep the model they receive as view state and mutate it (closing the
    // help pop-up flips DisplayContent), so hand out a copy per subscription.
    return request.pipe(map(data => ({ ...data })));
  }

  public getAllMasterHelp(): Observable<MasterHelpModel[]> {
    return this.http.get<MasterHelpModel[]>(`${this.configCoreUrl}/getAllMasterHelp`);
  }

  public UpdatedHelp(stepId: number, displayContent: boolean) {
    return this.http.get(`${this.configCoreUrl}/updatedHelp/${stepId}/${displayContent}`);
  }

  public UpdatedHelpById(MasterHelpId: number, displayContent: boolean) {
    return this.http.post(`${this.configCoreUrl}/updatedHelpById/${MasterHelpId}/${displayContent}`, {})
      .pipe(tap(() => {
        const cached = this.helpByIdValues.get(MasterHelpId);
        if (cached) {
          cached.DisplayContent = displayContent;
        }
      }));
  }

  // File Download
  public downloadFileByBlob(fileContent: any, fileName: string, contentType: string) {
    const blob: any = new Blob([fileContent], { type: contentType });
    const url = window.URL.createObjectURL(blob);
    const downloadLink = document.createElement('a');
    downloadLink.setAttribute('href', url);
    downloadLink.setAttribute('download', fileName);
    downloadLink.style.visibility = 'hidden';
    document.body.appendChild(downloadLink);
    downloadLink.click();
    document.body.removeChild(downloadLink);
  }
}
